import { DateRange, dateRangeFromString, DEFAULT_QUICK_RANGE, } from "@/dateRanges/dateRanges";
import { differenceInMinutes } from "date-fns";
import React, { createContext, Dispatch, SetStateAction, useContext } from "react";
import {
  DEFAULT_WINDOW_SIZE,
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
  SIX_HOURS,
  TEN_MINUTES,
  TWENTY_SECONDS,
  WINDOW_SIZES,
  WindowSize
} from "@/dateRanges/windowSizes";
import { DEFAULT_REFRESH_INTERVAL, REFRESH_INTERVALS, RefreshInterval } from "@/dateRanges/refreshIntervals";
import {
  useInitialOverrideUnlessInQueryParamsOnFirstLoad,
  useStateSyncedWithQueryParam
} from "@/dateRanges/UseInitialOverrideUnlessInQueryParamsOnFirstLoad";

type DateRangeContextType = {
  dateRange: DateRange;
  setDateRange: Dispatch<SetStateAction<DateRange>>;
  windowSize: WindowSize;
  setWindowSize: Dispatch<SetStateAction<WindowSize>>;
  refreshInterval: RefreshInterval;
  setRefreshInterval: Dispatch<SetStateAction<RefreshInterval>>;
};

export const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

const DATE_RANGE_QUERY_PARAM_KEY = "dateRange";
const WINDOW_SIZE_QUERY_PARAM_KEY = "windowSize";
const REFRESH_INTERVAL_QUERY_PARAM_KEY = "refreshInterval";

export const getAutoWindowSize = (dateRange: DateRange) => {
  const minutes = differenceInMinutes(dateRange.value.to(), dateRange.value.from());
  if (minutes <= 10) return TWENTY_SECONDS; // up to 10 minutes
  if (minutes <= 60 * 24) return ONE_MINUTE; // up to 1 day
  if (minutes <= 60 * 24 * 7) return TEN_MINUTES; // up to 7 days
  if (minutes <= 60 * 24 * 7 * 2) return { label: "20 minutes", value: "20m" }; // up to 2 weeks
  if (minutes <= 60 * 24 * 7 * 5) return { label: "40 minutes", value: "40m" }; // up to 5 weeks
  if (minutes <= 60 * 24 * 30 * 3) return ONE_HOUR; // up to 3 months
  if (minutes <= 60 * 24 * 30 * 12) return SIX_HOURS; // up to 1 year
  return ONE_DAY;
}

export const DateRangeContextProvider = ({ children }: any) => {

  const [dateRange, setDateRange] = useStateSyncedWithQueryParam(
    DATE_RANGE_QUERY_PARAM_KEY,
    DEFAULT_QUICK_RANGE,
    (dateRange) => dateRange.label,
    dateRangeFromString
  )

  const [windowSize, setWindowSize] = useStateSyncedWithQueryParam(
    WINDOW_SIZE_QUERY_PARAM_KEY,
    DEFAULT_WINDOW_SIZE,
    (windowSize) => windowSize.value,
    (paramValue) => WINDOW_SIZES.find((_windowSize) => _windowSize.value === paramValue)
  )

  const [refreshInterval, setRefreshInterval] = useStateSyncedWithQueryParam(
    REFRESH_INTERVAL_QUERY_PARAM_KEY,
    DEFAULT_REFRESH_INTERVAL,
    (refreshInterval) => refreshInterval.value.toString(),
    (paramValue) => REFRESH_INTERVALS.find((_refreshInterval) => paramValue && _refreshInterval.value === parseInt(paramValue))
  )

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        setDateRange,
        windowSize,
        setWindowSize,
        refreshInterval,
        setRefreshInterval,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};

const mergeContextWithOverrides = <ContextType extends Record<string, any>, >(
  context: ContextType,
  overrideValues: Partial<ContextType>
): ContextType => {
  Object.keys(overrideValues)
    .forEach(key => overrideValues[key] === undefined && delete overrideValues[key]);
  return {
    ...context,
    ...overrideValues
  }
}

type UseDateRangeOverrides = {
  overrideDefaultDateRange?: DateRange;
  overrideDefaultWindowSize?: WindowSize;
  overrideDefaultRefreshInterval?: RefreshInterval;
}

export const useDateRange = (
  { overrideDefaultDateRange, overrideDefaultWindowSize, overrideDefaultRefreshInterval }: UseDateRangeOverrides = {}
): DateRangeContextType => {
  const context = useContext(DateRangeContext);
  if (context === undefined) {
    throw new Error("Context must be used within a Provider");
  }

  const overrideDefaultDateRangeValue = useInitialOverrideUnlessInQueryParamsOnFirstLoad(
    DATE_RANGE_QUERY_PARAM_KEY,
    overrideDefaultDateRange,
    context.setDateRange
  )
  const overrideDefaultWindowSizeValue = useInitialOverrideUnlessInQueryParamsOnFirstLoad(
    WINDOW_SIZE_QUERY_PARAM_KEY,
    overrideDefaultWindowSize,
    context.setWindowSize
  )
  const overrideDefaultRefreshIntervalValue = useInitialOverrideUnlessInQueryParamsOnFirstLoad(
    REFRESH_INTERVAL_QUERY_PARAM_KEY,
    overrideDefaultRefreshInterval,
    context.setRefreshInterval
  )

  return mergeContextWithOverrides<DateRangeContextType>(
    context,
    {
      dateRange: overrideDefaultDateRangeValue,
      windowSize: overrideDefaultWindowSizeValue,
      refreshInterval: overrideDefaultRefreshIntervalValue
    }
  )
};
